import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { IS_PROD_BUILD, MONEY_SURFACE_ENABLED, PROGRAMS_SURFACE_ENABLED } from '../config/flags';
import { resolveGamificationPreviewAccess, type PreviewAccess } from './gamificationPreviewAccess';
import { startVisibilityAwareInterval } from '../utils/visibilityAwareInterval';
import { FOOTER_HIDDEN_SOCIALS_SHOWN_FLAG } from '../config/socialLinks';

let cachedFlags: Record<string, boolean> | null = null;
let lastAuthState: boolean | null = null;
// CORE-1386 — один запрос в полёте на всё приложение (веб). Кеш выше есть, но
// заполняется он только ответом, а до первого ответа каждый экземпляр хука шёл
// в сеть сам: главная монтирует шесть, плюс стартовый тик поллера — семь
// одинаковых `/api/features` за одну загрузку. Ключ — состояние входа: ответ,
// запрошенный гостем, не годится вошедшему, и такой вызов открывает свой.
let inflight: { auth: boolean | null; promise: Promise<void> } | null = null;

// CORE-356 — a session used to fetch flags once and cache forever, so a runtime
// flag flip (e.g. the AI/DM ChatApi cutover) only reached a user on a full reload
// — leaving open sessions on the old backend indefinitely. Refresh on an interval
// and on window-focus so an open app picks up a flip within ~a minute without a
// reload, shrinking every future cutover's transition window from "until reload".
const REFRESH_MS = 45_000;
const subscribers = new Set<(f: Record<string, boolean>) => void>();
let backgroundStarted = false;

function flagsEqual(a: Record<string, boolean> | null, b: Record<string, boolean>): boolean {
  if (!a) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every(k => a[k] === b[k]);
}

async function fetchFlags(): Promise<void> {
  try {
    const f = await api.getFeatures();
    if (flagsEqual(cachedFlags, f)) return; // no change → no re-render
    cachedFlags = f;
    subscribers.forEach(cb => cb(f));
  } catch {
    // Keep existing cache on error (offline / transient).
  }
}

// Поллер и фокус зовут без аргумента — им подходит текущее состояние входа.
function loadFlags(auth: boolean | null = lastAuthState): Promise<void> {
  if (inflight && inflight.auth === auth) return inflight.promise;
  const promise = fetchFlags().finally(() => {
    if (inflight?.promise === promise) inflight = null;
  });
  // На телефоне — как было: каждый вызов идёт в сеть сам (задача только про веб).
  if (Platform.OS === 'web') inflight = { auth, promise };
  return promise;
}

// One shared poller + focus/visibility refetch for the whole app — not one per
// hook instance. Bound once; lives for the app's lifetime.
function startBackgroundRefresh(): void {
  if (backgroundStarted) return;
  backgroundStarted = true;
  // CORE-1102 — this poller lives for the app's lifetime, so before it also
  // ran for the lifetime of a forgotten background tab. The helper stops it
  // while the page is hidden and ticks on the way back, which is what the
  // `visibilitychange` listener here used to do by hand — hence its removal:
  // keeping both would fetch twice on every return to the tab.
  startVisibilityAwareInterval({ onTick: loadFlags, intervalMs: REFRESH_MS });
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.addEventListener('focus', () => { loadFlags(); });
  }
}

/**
 * CORE-901 — whether `/api/features` has answered at least once in this session.
 *
 * `useFeatureFlags` hands out `{}` until the first response lands, and an empty
 * map is indistinguishable from «every flag is off». That is harmless for a gate
 * that merely hides a tab — it appears a moment later — but not for one that
 * navigates the visitor away on a denial. Callers that act irreversibly must
 * wait for this to be true before treating a missing flag as a «no».
 */
export function areFeatureFlagsLoaded(): boolean {
  return cachedFlags !== null;
}

export function useFeatureFlags() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>(cachedFlags || {});

  useEffect(() => {
    if (authLoading) return;

    subscribers.add(setFlags);

    // Re-fetch when auth state changes or no cache; otherwise serve the cache.
    if (cachedFlags && lastAuthState === isAuthenticated) {
      setFlags(cachedFlags);
    } else {
      lastAuthState = isAuthenticated;
      loadFlags(isAuthenticated);
    }
    // CORE-1386 — после запроса выше, а не до: стартовый тик поллера тогда
    // присоединяется к нему, а не открывает второй.
    startBackgroundRefresh();

    return () => { subscribers.delete(setFlags); };
  }, [isAuthenticated, authLoading]);

  return flags;
}

/** Visible-for-testing — the single-flight loader and a state reset. */
export const _loadFlagsForTest = loadFlags;
export function _resetFeatureFlagsForTest(): void {
  cachedFlags = null;
  lastAuthState = null;
  inflight = null;
}

/**
 * AMB-231 — gates everything that surfaces the «Live-практика» entity
 * (host-led practice rooms, BE kind code `LivePractice`, FE type
 * `RoomResponse`). Default is **disabled**: a missing or falsey
 * `live_practices` flag hides every CTA / listing / direct-route entry
 * for ordinary users. The host's own «Быстрая комната» path stays open
 * regardless — that's the master-only Quick Room exception called out
 * in the ticket.
 *
 * Flag name follows the BE seed convention (snake_case, matches
 * `palabra_translation` / `screen_sharing` / `auth_facebook`); bracket
 * access keeps TypeScript happy since flags is `Record<string, boolean>`.
 *
 * Returns a single boolean so call-sites read as
 *   `if (!livePracticesEnabled) return null;`
 * rather than spelling out the flag name and explicit-`=== true` check
 * everywhere.
 */
export function useLivePracticesEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['live_practices'] === true;
}

/**
 * CORE-1043 — whether the app offers to record a Live at all: the wizard's
 * «Запись» step, the in-room record button and the CORE-816 auto-start.
 *
 * A per-environment switch rather than a code change, because whether recording
 * works is a property of the environment, not of the build: dev records through
 * our own sidecar (CORE-1040) and LiveKit egress (CORE-1042), while staging and
 * prod still depend on Agora Cloud Recording, which went silent for a day in
 * August. ON for dev, OFF for staging and prod.
 *
 * Absent / falsey → OFF, like every other gate here, so the switch is explicit
 * on the environment that has recording and needs no row on the ones that don't.
 *
 * The gate is deliberately UI-only. It removes the OFFER of recording; it does
 * not stop the server. A master's live still starts its recording when the
 * broadcast goes on air — that call comes from the server, not from a button —
 * so this flag must never be read as «nothing is being recorded». For the same
 * reason the participant warning «встреча записывается» is NOT behind it: that
 * is consent, and hiding it while the server can still record would be a lie.
 */
export function useLiveRecordingEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['live_recording'] === true;
}

/**
 * CORE-1345 — показ экрана в комнате (веб, запуск только с компьютера). Выключено —
 * кнопки нет; сервер без флага отвечает на запуск 404. Смотреть идущий показ можно
 * и при выключенном флаге: снимок и событие хаба приходят всё равно.
 */
export function useRoomScreenSharingEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['room_screen_sharing'] === true;
}

/** CORE-1598 — RNNoise on the room microphone (web). Off until the flag exists and is on. */
export function useMicNoiseSuppressionEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['mic_noise_suppression'] === true;
}

/**
 * CORE-371 (P7.3) — routes AI companion / Wisdom Council chat to Agape.ChatApi's
 * SignalR hub (`/hubs/chat`) instead of GetStream. Runtime gate (from
 * `/api/features`) so a flip reaches installed bundles without an EAS rebuild.
 * Absent → false (OFF = talk to Stream, current behaviour). Enable only once the
 * ChatApi AI path is validated end-to-end and in lockstep with awesome-chat-api
 * `USE_CHATAPI` + the ChatApi inbound trigger. Applies to `ai` / `wisdom-council`
 * channels only; circles, DMs and live chat are unaffected.
 */
export function useChatApiEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['ai_chat_via_chat_api'] === true;
}

/**
 * CORE-356 — route friend DMs through Agape.ChatApi instead of GetStream.
 * Runtime gate (`/api/features`, absent → false = Stream). Gated behind the DM
 * backend track (channel type + mirroring + auto-provisioning) landing first.
 */
export function useDmChatApiEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['dm_chat_via_chat_api'] === true;
}

/**
 * CORE-541 (live-unification) — gates the NEW unified Live-session interface
 * (single «Live-сессия» entity; creation wizard, unified studio/broadcast/end
 * flow) built on the extended watch-party contract. Runtime, NOT compile-time,
 * so the new bundle can ship to prod with the flow OFF and be switched on by a
 * pure flag-flip in the admin panel — no web redeploy and no EAS rebuild — and
 * rolled back the same way if a regression appears.
 *
 * Absent / falsey → OFF: every unified-Live entry point stays hidden and the
 * platform keeps using the legacy live/room flows (LiveStreamModal /
 * CreateRoomModal / quick-room), satisfying the epic's back-compat AC. Admin
 * creates / enables `unified_live_session` (snake_case, BE seed convention) to
 * turn the new flow on per environment once it's validated on dev.
 */
export function useUnifiedLiveEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['unified_live_session'] === true;
}

/**
 * CORE-871 — gates the whole «Программы» section: paid programs and paid
 * practices of a master (create, publish, buy, take).
 *
 * Absent / falsey → OFF, which is what every environment has today: the
 * «Программы» tab appears neither on a master's profile nor on a user's, and
 * the public program page is unreachable.
 *
 * OFF by default for a concrete reason, not caution: the epic ships in waves
 * and the purchase half waits on the payments contour (CORE-13). Until that
 * lands, a visible section would advertise a «Купить программу» button that
 * cannot take money.
 *
 * This is the PLATFORM gate. It is not the same thing as the master's own
 * «Показывать раздел в профиле» toggle (CORE-889), which decides whether one
 * particular master publishes their section — and which, unlike this flag,
 * never touches the access of people who already bought.
 */
export function useMasterProgramsEnabled(): boolean {
  const flags = useFeatureFlags();
  // CORE-1157 — на телефоне раздела нет вовсе, чем бы ни ответил `/api/features`.
  // Флаг нужен вебу и однажды там будет включён; без этой строки тот же тумблер
  // открыл бы «Программы» и в приложении. Платформа перекрывает флаг.
  if (!PROGRAMS_SURFACE_ENABLED) return false;
  return flags['master_programs'] === true;
}

/**
 * CORE-249 / CORE-664 — gates «Путь Света»: the profile tab and the progress
 * card on the profile cover.
 *
 * This used to be `LIGHT_PATH_ENABLED = !IS_PROD_BUILD` in config/flags.ts — a
 * compile-time constant, which meant «turn gamification on for prod» cost a web
 * deploy plus an EAS rebuild, and rolling back cost another pair. Runtime flag
 * instead: an admin flips `light_path` in the admin panel and installed bundles
 * pick it up within the poller's ~45s (or on window focus), with rollback the
 * same way.
 *
 * Absent / falsey → OFF, which is what prod has today, so the switch to a
 * runtime gate does not change prod behaviour by itself.
 */
export function useLightPathEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['light_path'] === true;
}

/**
 * CORE-901 — gates the hidden gamification QA-preview catalogue
 * (GamificationPreviewScreen), which renders every gamification state on
 * fixtures so QA can eyeball rare layouts without seeding a matching profile in
 * the DB. Modelled on {@link useLightPathEnabled}.
 *
 * Access = the runtime flag `gamification_preview` (snake_case, admin-panel
 * flip, absent → off) OR — for QA convenience without a flip — the
 * `?qa=gamification` URL param, but ONLY on a non-prod web build. On prod the
 * URL override is inert, so the single production entry stays the flag: an
 * admin-only flip, never a guessable URL. The screen paints fixtures, not the
 * viewer's data, so nothing private leaks even when it is open.
 *
 * Returns THREE states, not two: `true` / `false` / `null` = «not known yet».
 * The distinction matters because the caller acts on a denial by navigating
 * away, and `/api/features` arrives asynchronously — treating the first render's
 * empty flag map as a denial would bounce a legitimate visitor off the screen
 * before the flag it was granted by ever arrived. That failure would only show
 * up on prod, where the flag is the sole entry; on dev the synchronous URL
 * override masks it.
 */
export function useGamificationPreviewEnabled(): PreviewAccess {
  const flags = useFeatureFlags();
  const urlOverride =
    !IS_PROD_BUILD &&
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('qa') === 'gamification';

  return resolveGamificationPreviewAccess({
    flagsLoaded: areFeatureFlagsLoaded(),
    flag: flags['gamification_preview'],
    urlOverride,
  });
}

/**
 * CORE-964 — гейт экрана «Фонд / Финансы».
 *
 * Раньше это была константа `COMING_SOON = true` внутри самого экрана: чтобы
 * открыть его, нужен был релиз, а чтобы закрыть обратно — ещё один. Теперь
 * рантайм-флаг, как у остальных крупных поверхностей: админ включает
 * `fund_screen` на нужном контуре, и установленные бандлы подхватывают это
 * за ~45 секунд поллера.
 *
 * Отсутствует / выключен → экран показывает «Coming Soon», то есть ровно
 * сегодняшнее поведение прода. Включение — отдельное решение по контурам.
 */
export function useFundScreenEnabled(): boolean {
  const flags = useFeatureFlags();
  // CORE-1157 — денежные экраны в мобильном релизе не показываем (см.
  // MONEY_SURFACE_ENABLED); на нативе ответ отрицательный до правки кода.
  if (!MONEY_SURFACE_ENABLED) return false;
  return flags['fund_screen'] === true;
}

/**
 * CORE-1186 — гейт раздела «Финансы» (CORE-1135): пункт в шапке и экраны
 * `/finance`, `/wallet`, `/fund/payout-details`.
 *
 * Пункт стоял в шапке безусловно, а экран рисовался всегда — флаги внутри
 * решали лишь, ходить ли за данными. На проде это давало страницу с пустыми
 * прочерками и кнопкой «Реквизиты», ведущей на форму ИНН/СНИЛС в РФ-контур.
 *
 * Флаг свой, а не `fund_screen`: тот про экран «Фонд», а это личные финансы
 * пользователя. Одна кнопка на оба означала бы, что открыть одно нельзя, не
 * открыв другое.
 *
 * Отсутствует / выключен → раздела нет: ни пункта, ни экранов.
 */
export function useFinanceScreenEnabled(): boolean {
  const flags = useFeatureFlags();
  // CORE-1157 — «Финансы» целиком денежная поверхность, в мобильном релизе её
  // нет независимо от ответа сервера.
  if (!MONEY_SURFACE_ENABLED) return false;
  return flags['finance_screen'] === true;
}

/**
 * CORE-962 / CORE-964 — гейт денежной части реферальной программы: блок
 * «Реферальная программа» с суммами и историей начислений.
 *
 * Это этап C по CORE-969, и флаг тот же, которым закрыт пользовательский API
 * на бэкенде (`referral_user_ui`). Совпадение не случайно: экран, открытый
 * без своего API, показал бы 404 вместо цифр, а API, открытый без экрана,
 * никому не виден. Один флаг на обе стороны — одно состояние, а не два.
 *
 * Отсутствует / выключен → блок не рендерится вовсе.
 */
export function useReferralUiEnabled(): boolean {
  const flags = useFeatureFlags();
  // CORE-1157 — суммы и история начислений относятся к денежному контуру.
  if (!MONEY_SURFACE_ENABLED) return false;
  return flags['referral_user_ui'] === true;
}

/**
 * CORE-1156 — этап выплат по CORE-969 (`referral_payout`), последний в цепочке.
 *
 * Начисления и выплаты включаются РАЗДЕЛЬНО и намеренно: программа стартует в
 * состоянии «деньги копятся, забрать пока нельзя» — рублёвые выплаты всё равно
 * отменяются налоговым правилом до CORE-1069. Пока флаг выключен, интерфейс
 * обязан это говорить: «Доступно к выплате», «Выплаты формируются автоматически»
 * и дата следующей выплаты — обещания, которых система не выполнит.
 *
 * Отсутствует / выключен → суммы показываем, выплату называем недоступной.
 */
export function useReferralPayoutEnabled(): boolean {
  const flags = useFeatureFlags();
  // CORE-1157 — выплаты тем более: на телефоне нет ни сумм, ни реквизитов.
  if (!MONEY_SURFACE_ENABLED) return false;
  return flags['referral_payout'] === true;
}

/**
 * CORE-1715 — переключатель языка в вебе: шапка на компьютере, её мобильная
 * шторка и шапка `/landing`. Решение дейлика 15.10.2026 — пока скрыть, а
 * вернуть флагом `language_switcher` из админки, без релиза.
 *
 * Отсутствует / выключен → переключателя нет. Пока `/api/features` не ответил,
 * карта флагов пуста и ответ тоже «нет» — то есть переключатель не мелькает на
 * загрузке и после обновления страницы, а при включённом флаге появляется с
 * первым ответом.
 *
 * Пока флаг выключен, интерфейс русский (`useLanguageSwitcherGate`):
 * иначе с сохранённым en/es не вернуться обратно. Сам выбор человека не
 * стирается — с включением флага его язык возвращается.
 *
 * Тот же флаг прячет переключатель и в приложении — строку «Язык» в
 * бургер-меню и блок «Язык» в «Настройках» (CORE-1306). Решение то же:
 * пока скрыть, вернуть тумблером из админки без нового билда.
 */
export function useLanguageSwitcherEnabled(): boolean {
  const flags = useFeatureFlags();
  return flags['language_switcher'] === true;
}

/**
 * CORE-1768 — вернуть в подвал сети из `FOOTER_HIDDEN_SOCIALS` (сейчас
 * Instagram). Выключен или не загружен — скрыты, для всех и везде.
 */
export function useFooterHiddenSocialsShown(): boolean {
  const flags = useFeatureFlags();
  return flags[FOOTER_HIDDEN_SOCIALS_SHOWN_FLAG] === true;
}
